<template>
  <div>
    <title-bar :title="pageTitle">
      <el-button type="default" @click="cancel">返回列表</el-button>
      <el-button type="primary" :loading="loading" @click="save">保存</el-button>
    </title-bar>
    <div class="page-body">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="基本信息" name="basic">
          <el-alert
            type="info"
            :closable="false"
            show-icon
            style="margin-bottom: 16px"
            title="版本编码需与设备上报的软件版本 SV 一致（传统设备）；PLC 可使用 PLC_DEFAULT。"
          ></el-alert>
          <el-form ref="basicForm" :model="form" :rules="basicRules" label-width="120px" style="max-width: 720px">
            <el-form-item label="版本编码" prop="versionCode">
              <el-input v-model="form.versionCode" placeholder="如 04000501 / PLC_DEFAULT" :disabled="!!form.id"></el-input>
            </el-form-item>
            <el-form-item label="版本名称" prop="versionName">
              <el-input v-model="form.versionName" placeholder="如 箱体版-告警bit"></el-input>
            </el-form-item>
            <el-form-item label="备注">
              <el-input type="textarea" :rows="3" v-model="form.remark" maxlength="500" show-word-limit></el-input>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="输入寄存器（状态）" name="input">
          <div class="offset-toolbar">
            <el-form inline label-width="140px" class="offset-form">
              <el-form-item label="输入寄存器偏移量">
                <el-input-number v-model="form.inputOffset" :min="0" :controls="false"></el-input-number>
                <span class="form-tip">解析时实际地址 = 偏移量 + 配置地址</span>
              </el-form-item>
            </el-form>
            <el-button type="primary" size="small" icon="fa fa-plus" @click="addInput">新增</el-button>
          </div>
          <el-table :data="form.inputRegisters" border stripe class="reg-table">
            <el-table-column label="地址" width="80" align="center">
              <template slot-scope="{ row }">
                <el-input-number v-model="row.address" :min="0" size="small" :controls="false" style="width: 100%"></el-input-number>
              </template>
            </el-table-column>
            <el-table-column label="实际地址" width="80" align="center">
              <template slot-scope="{ row }">
                {{ actualAddress(form.inputOffset, row.address) }}
              </template>
            </el-table-column>
            <el-table-column label="位号" width="70" align="center">
              <template slot-scope="{ row }">
                <el-select v-model="row.bitIndex" clearable placeholder="空" size="small" style="width: 100%">
                  <el-option v-for="n in bitOptions" :key="n" :label="String(n)" :value="n"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="信号键" min-width="170">
              <template slot-scope="{ row }">
                <el-select v-model="row.signalKey" filterable allow-create size="small" style="width: 100%" @change="onSignalKeyChange(row)">
                  <el-option v-for="opt in signalKeyOptions" :key="opt.codeValue" :label="opt.codeText" :value="opt.codeValue"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="显示名" min-width="120">
              <template slot-scope="{ row }">
                <el-input v-model="row.signalName" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="数据类型" width="140">
              <template slot-scope="{ row }">
                <el-select v-model="row.dataType" size="small" style="width: 100%">
                  <el-option v-for="opt in dataTypeOptions" :key="opt.codeValue" :label="opt.codeText" :value="opt.codeValue"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="单位" width="60" align="center">
              <template slot-scope="{ row }">
                <el-input v-model="row.unit" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="启用" width="70" align="center">
              <template slot-scope="{ row }">
                <el-switch v-model="row.valid"></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="70" align="center" fixed="right">
              <template slot-scope="{ $index }">
                <el-button type="text" class="color-error" @click="removeInput($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="保持寄存器（参数）" name="holding">
          <div class="offset-toolbar">
            <el-form inline label-width="140px" class="offset-form">
              <el-form-item label="保持寄存器偏移量">
                <el-input-number v-model="form.holdingOffset" :min="0" :controls="false"></el-input-number>
                <span class="form-tip">读写时实际地址 = 偏移量 + 配置地址</span>
              </el-form-item>
            </el-form>
            <div class="offset-actions">
              <el-button type="primary" size="small" icon="fa fa-plus" @click="addHolding">新增</el-button>
              <el-button
                type="default"
                size="small"
                icon="fa fa-arrow-up"
                title="上移"
                :disabled="!canMoveHoldingUp"
                @click="moveHolding('up')"
              ></el-button>
              <el-button
                type="default"
                size="small"
                icon="fa fa-arrow-down"
                title="下移"
                :disabled="!canMoveHoldingDown"
                @click="moveHolding('down')"
              ></el-button>
              <el-button
                type="default"
                size="small"
                icon="fa fa-angle-double-up"
                title="置顶"
                :disabled="!canMoveHoldingUp"
                @click="moveHolding('top')"
              ></el-button>
              <el-button
                type="default"
                size="small"
                icon="fa fa-angle-double-down"
                title="置底"
                :disabled="!canMoveHoldingDown"
                @click="moveHolding('bottom')"
              ></el-button>
              <el-button type="default" size="small" icon="fa fa-eye" @click="openHoldingPreview">预览</el-button>
            </div>
          </div>
          <el-table
            ref="holdingTable"
            :data="form.holdingRegisters"
            border
            stripe
            highlight-current-row
            class="reg-table"
            @current-change="onHoldingCurrentChange"
          >
            <el-table-column label="地址" width="80" align="center">
              <template slot-scope="{ row }">
                <el-input-number v-model="row.address" :min="0" size="small" :controls="false" style="width: 100%"></el-input-number>
              </template>
            </el-table-column>
            <el-table-column label="实际地址" width="80" align="center">
              <template slot-scope="{ row }">
                {{ actualAddress(form.holdingOffset, row.address) }}
              </template>
            </el-table-column>
            <el-table-column label="参数名称" min-width="140">
              <template slot-scope="{ row }">
                <el-input v-model="row.paramName" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="数据类型" width="140">
              <template slot-scope="{ row }">
                <el-select v-model="row.dataType" size="small" style="width: 100%">
                  <el-option v-for="opt in dataTypeOptions" :key="opt.codeValue" :label="opt.codeText" :value="opt.codeValue"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="输入组件" width="110">
              <template slot-scope="{ row }">
                <el-select v-model="row.inputComponent" size="small" style="width: 100%" placeholder="请选择">
                  <el-option v-for="opt in inputComponentOptions" :key="opt.codeValue" :label="opt.codeText" :value="opt.codeValue"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="单位" width="60" align="center">
              <template slot-scope="{ row }">
                <el-input v-model="row.unit" size="small" :disabled="row.inputComponent === 'SWITCH'"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="最小值" width="70" align="center">
              <template slot-scope="{ row }">
                <el-input-number
                  v-model="row.minValue"
                  size="small"
                  :controls="false"
                  style="width: 100%"
                  :disabled="row.inputComponent === 'SWITCH'"
                ></el-input-number>
              </template>
            </el-table-column>
            <el-table-column label="最大值" width="70" align="center">
              <template slot-scope="{ row }">
                <el-input-number
                  v-model="row.maxValue"
                  size="small"
                  :controls="false"
                  style="width: 100%"
                  :disabled="row.inputComponent === 'SWITCH'"
                ></el-input-number>
              </template>
            </el-table-column>
            <el-table-column label="备注" min-width="140">
              <template slot-scope="{ row }">
                <el-input v-model="row.remark" size="small"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="启用" width="70" align="center">
              <template slot-scope="{ row }">
                <el-switch v-model="row.valid"></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="授权" width="70" align="center">
              <template slot-scope="{ row }">
                <el-switch v-model="row.authorized"></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="70" align="center" fixed="right">
              <template slot-scope="{ $index }">
                <el-button type="text" class="color-error" @click="removeHolding($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>

      <div class="button-container">
        <el-button type="primary" :loading="loading" @click="save">保存</el-button>
        <el-button type="default" @click="cancel">取消</el-button>
      </div>
    </div>

    <el-dialog
      title="参数设置预览"
      :visible.sync="previewVisible"
      width="960px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-alert
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
        title="根据当前启用的保持寄存器配置渲染，仅预览交互组件效果，不会真正下发设备。"
      ></el-alert>
      <el-table
        stripe
        size="small"
        :data="previewRecords"
        border
        height="60vh"
        class="holding-preview-table"
      >
        <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
        <el-table-column prop="paramName" label="参数名称" min-width="160">
          <template slot-scope="{ row }">
            <span>{{ row.paramName || '未命名参数' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="实际地址" width="90" align="center">
          <template slot-scope="{ row }">
            {{ row.actualAddress }}
          </template>
        </el-table-column>
        <el-table-column label="参数值" width="200" align="center">
          <template slot-scope="{ row }">
            <el-switch
              v-if="row.inputComponent === 'SWITCH'"
              v-model="row.paramValue"
              :active-value="1"
              :inactive-value="0"
              active-text="ON"
              inactive-text="OFF"
            ></el-switch>
            <el-input-number
              v-else
              v-model="row.paramValue"
              size="small"
              :controls="false"
              :min="row.minValue"
              :max="row.maxValue"
              style="width: 140px"
            ></el-input-number>
          </template>
        </el-table-column>
        <el-table-column prop="unit" label="单位" width="60" align="center"></el-table-column>
        <el-table-column prop="remark" label="参数说明" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="操作" width="90" align="center" fixed="right">
          <template>
            <el-button type="text" disabled>设置</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button type="default" disabled>读取参数</el-button>
        <el-button type="primary" @click="previewVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import TitleBar from "@/components/base/TitleBar";
import BaseVue from "@/components/base/BaseVue";

function emptyVersion() {
  return {
    id: null,
    versionCode: "",
    versionName: "",
    remark: "",
    inputOffset: 0,
    holdingOffset: 0,
    inputRegisters: [],
    holdingRegisters: []
  };
}

export default {
  name: "HwProtocolVersionEdit",
  components: { TitleBar },
  extends: BaseVue,
  data() {
    return {
      activeTab: "basic",
      dataTypeOptions: [],
      inputComponentOptions: [],
      signalKeyOptions: [],
      bitOptions: Array.from({ length: 32 }, (_, i) => i),
      form: emptyVersion(),
      previewVisible: false,
      previewRecords: [],
      currentHoldingRow: null,
      basicRules: {
        versionCode: [{ required: true, message: "请输入版本编码", trigger: "blur" }],
        versionName: [{ required: true, message: "请输入版本名称", trigger: "blur" }]
      }
    };
  },
  computed: {
    pageTitle() {
      return this.form.id ? "编辑协议版本 - " + (this.form.versionCode || "") : "新增协议版本";
    },
    holdingCurrentIndex() {
      const list = this.form.holdingRegisters || [];
      if (!this.currentHoldingRow || !list.length) {
        return -1;
      }
      return list.indexOf(this.currentHoldingRow);
    },
    canMoveHoldingUp() {
      return this.holdingCurrentIndex > 0;
    },
    canMoveHoldingDown() {
      const list = this.form.holdingRegisters || [];
      return this.holdingCurrentIndex >= 0 && this.holdingCurrentIndex < list.length - 1;
    }
  },
  created() {
    this.loadCodes();
    const id = this.$route.query.id;
    if (id) {
      this.loadDetail(id);
    }
  },
  methods: {
    loadCodes() {
      this.getCodes("HW_DATA_TYPE").then(dr => {
        this.dataTypeOptions = dr.rows || [];
      });
      this.getCodes("HW_INPUT_COMPONENT").then(dr => {
        this.inputComponentOptions = dr.rows || [];
      });
      // 测量信号键 + 告警类型（告警用枚举名/ext1 作 signalKey，如 FAN_ERROR）
      Promise.all([
        this.getCodes("HW_SIGNAL_KEY"),
        this.getCodes("WARNING_TYPE")
      ]).then(([sigDr, warnDr]) => {
        const measure = sigDr.rows || [];
        const warnings = (warnDr.rows || []).map(w => ({
          codeValue: w.ext1 || w.codeValue,
          codeText: (w.codeText || w.codeValue) + "（告警）",
          ext1: w.ext1,
          ext2: w.ext2
        }));
        this.signalKeyOptions = measure.concat(warnings);
      });
    },
    actualAddress(offset, address) {
      const o = offset == null || offset === "" ? 0 : Number(offset);
      const a = address == null || address === "" ? 0 : Number(address);
      if (isNaN(o) || isNaN(a)) {
        return "-";
      }
      return o + a;
    },
    openHoldingPreview() {
      const list = (this.form.holdingRegisters || []).filter(row => row.valid !== false);
      if (!list.length) {
        this.$message.warning("请先配置有效的保持寄存器参数");
        return;
      }
      this.previewRecords = list.map((row, idx) => {
        const isSwitch = row.inputComponent === "SWITCH";
        let paramValue = 0;
        if (!isSwitch && row.minValue != null && row.minValue !== "") {
          paramValue = Number(row.minValue);
        }
        return {
          id: row.id || "p" + idx,
          paramName: row.paramName,
          address: row.address,
          actualAddress: this.actualAddress(this.form.holdingOffset, row.address),
          dataType: row.dataType,
          inputComponent: row.inputComponent || "NUMBER",
          unit: isSwitch ? "" : row.unit,
          minValue: row.minValue,
          maxValue: row.maxValue,
          remark: row.remark,
          paramValue: paramValue
        };
      });
      this.previewVisible = true;
    },
    loadDetail(id) {
      this.loading = true;
      this.postForm("/hw-protocol/getById", { id: id }, true)
        .then(dr => {
          const bean = dr.bean;
          if (!bean) {
            this.$message.warning("未找到版本数据，按新增处理");
            this.form = emptyVersion();
            return;
          }
          this.form = bean;
          this.currentHoldingRow = null;
          if (this.form.inputOffset == null) {
            this.$set(this.form, "inputOffset", 0);
          }
          if (this.form.holdingOffset == null) {
            this.$set(this.form, "holdingOffset", 0);
          }
          if (!this.form.inputRegisters) {
            this.form.inputRegisters = [];
          }
          if (!this.form.holdingRegisters) {
            this.form.holdingRegisters = [];
          }
          this.normalizeRegisterValid(this.form.inputRegisters);
          this.normalizeRegisterValid(this.form.holdingRegisters, true);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSignalKeyChange(row) {
      const opt = this.signalKeyOptions.find(i => i.codeValue === row.signalKey);
      if (opt && !row.signalName) {
        row.signalName = opt.codeText;
      }
    },
    addrBitKey(row) {
      const bit = row.bitIndex === null || row.bitIndex === undefined || row.bitIndex === "" ? "" : String(row.bitIndex);
      return String(row.address) + "#" + bit;
    },
    findDuplicateAddrBit(list) {
      const map = {};
      for (let i = 0; i < list.length; i++) {
        const row = list[i];
        if (row.address === null || row.address === undefined) {
          continue;
        }
        const key = this.addrBitKey(row);
        if (map[key] != null) {
          return {
            address: row.address,
            bitIndex: row.bitIndex === null || row.bitIndex === undefined || row.bitIndex === "" ? "空" : row.bitIndex,
            index: i + 1,
            firstIndex: map[key] + 1
          };
        }
        map[key] = i;
      }
      return null;
    },
    findDuplicateAddress(list) {
      const map = {};
      for (let i = 0; i < list.length; i++) {
        const row = list[i];
        if (row.address === null || row.address === undefined) {
          continue;
        }
        const key = String(row.address);
        if (map[key] != null) {
          return {
            address: row.address,
            index: i + 1,
            firstIndex: map[key] + 1
          };
        }
        map[key] = i;
      }
      return null;
    },
    normalizeRegisterValid(list, withAuthorized) {
      (list || []).forEach(row => {
        if (row.valid === null || row.valid === undefined) {
          this.$set(row, "valid", true);
        }
        if (withAuthorized && (row.authorized === null || row.authorized === undefined)) {
          this.$set(row, "authorized", true);
        }
      });
    },
    addInput() {
      this.form.inputRegisters.push({
        address: 0,
        bitIndex: null,
        signalKey: "",
        signalName: "",
        dataType: "INT_16",
        unit: "",
        valid: true
      });
    },
    removeInput(index) {
      this.form.inputRegisters.splice(index, 1);
    },
    addHolding() {
      const row = {
        address: 0,
        paramName: "",
        dataType: "INT_16",
        inputComponent: "NUMBER",
        unit: "",
        minValue: null,
        maxValue: null,
        remark: "",
        valid: true,
        authorized: true
      };
      this.form.holdingRegisters.push(row);
      this.$nextTick(() => {
        this.setHoldingCurrentRow(row);
      });
    },
    removeHolding(index) {
      const removed = this.form.holdingRegisters[index];
      this.form.holdingRegisters.splice(index, 1);
      if (removed === this.currentHoldingRow) {
        this.setHoldingCurrentRow(null);
      }
    },
    onHoldingCurrentChange(row) {
      this.currentHoldingRow = row || null;
    },
    setHoldingCurrentRow(row) {
      this.currentHoldingRow = row || null;
      if (this.$refs.holdingTable) {
        this.$refs.holdingTable.setCurrentRow(row || null);
      }
    },
    moveHolding(action) {
      const list = this.form.holdingRegisters;
      const index = this.holdingCurrentIndex;
      if (index < 0 || !list || !list.length) {
        this.$message.warning("请先选中一行保持寄存器");
        return;
      }
      let targetIndex = index;
      if (action === "up") {
        targetIndex = index - 1;
      } else if (action === "down") {
        targetIndex = index + 1;
      } else if (action === "top") {
        targetIndex = 0;
      } else if (action === "bottom") {
        targetIndex = list.length - 1;
      }
      if (targetIndex < 0 || targetIndex >= list.length || targetIndex === index) {
        return;
      }
      const row = list.splice(index, 1)[0];
      list.splice(targetIndex, 0, row);
      this.$nextTick(() => {
        this.setHoldingCurrentRow(row);
      });
    },
    save() {
      this.$refs.basicForm.validate(yes => {
        if (!yes) {
          this.activeTab = "basic";
          this.$message.warning("请完善基本信息");
          return;
        }
        const inputDup = this.findDuplicateAddrBit(this.form.inputRegisters || []);
        if (inputDup) {
          this.activeTab = "input";
          this.$message.warning(
            "输入寄存器第" + inputDup.index + "行与第" + inputDup.firstIndex +
            "行地址+位号重复（" + inputDup.address + " / 位号" + inputDup.bitIndex + "）"
          );
          return;
        }
        const holdingDup = this.findDuplicateAddress(this.form.holdingRegisters || []);
        if (holdingDup) {
          this.activeTab = "holding";
          this.$message.warning(
            "保持寄存器第" + holdingDup.index + "行与第" + holdingDup.firstIndex +
            "行地址重复（" + holdingDup.address + "）"
          );
          return;
        }
        this.loading = true;
        this.postJson("/hw-protocol/save", this.form, true)
          .then(dr => {
            this.form = dr.bean || this.form;
            this.currentHoldingRow = null;
            if (!this.form.inputRegisters) {
              this.form.inputRegisters = [];
            }
            if (!this.form.holdingRegisters) {
              this.form.holdingRegisters = [];
            }
            this.$router.replace({ path: "/hwProtocolVersionEdit", query: { id: this.form.id } });
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    cancel() {
      this.$router.push("/hwProtocolVersionList");
    }
  }
};
</script>

<style scoped>
.offset-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.offset-form {
  margin-bottom: 0;
}
.offset-form >>> .el-form-item {
  margin-bottom: 0;
}
.offset-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.form-tip {
  color: #909399;
  font-size: 12px;
  margin-left: 8px;
}
.button-container {
  margin-top: 24px;
  text-align: center;
}
.reg-table >>> .cell {
  padding: 0 2px;
}
.holding-preview-table {
  width: 100%;
}
</style>
